import { FetchResult, useApolloClient, useQuery } from "@apollo/client";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Tabs, Tab, Box, styled, Collapse } from "@mui/material";
import _ from "lodash";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { ReactElement, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  GetActiveCampaignContactQuery,
  GetActiveCampaignContactQueryVariables,
  GetCampaignForTexterQuery,
  GetCampaignForTexterQueryVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import { GET_ACTIVE_CAMPAIGN_CONTACT } from "~frontend/graphql/campaignContacts";
import { GET_CAMPAIGN_FOR_TEXTER } from "~frontend/graphql/campaigns";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccountSupervolunteerConfig } from "~frontend/providers/SubaccountProvider";
import { useTexterSettings } from "~frontend/providers/TexterSettingsProvider";
import { palette } from "~frontend/theme";
import { formatContactData } from "~frontend/util/contactFields";
import { graphqlErrorHasCode } from "~frontend/util/errors";
import useMousetrap from "~frontend/util/useMousetrap";

import NeedsResponseBullet from "../NeedsResponseBullet";
import { useThreadsContext } from "../ThreadsProvider";
import ActiveCampaignSwitcher from "./ActiveCampaignSwitcher";
import Buttons from "./Buttons";
import ContactDetails from "./ContactDetails";
import ContactHistory from "./ContactHistory";
import ResponseScripts from "./ResponseScripts";
import SendMessageBox from "./SendMessageBox";
import type { SendMessageBoxHandle } from "./SendMessageBox";
import Tags from "./Tags";
import UnsavedChanges from "./UnsavedChanges";
import useContextCampaignAndContact from "./contextCampaign";
import useThreadMutations, { ThreadMutations } from "./mutations";
import {
  Message,
  TagAppliedBy,
  Thread,
  UnsavedTag,
  updateUnsavedTagsForNewResponseScriptTags,
} from "./types";

const StyledTabs = styled(Tabs)({
  backgroundColor: palette.white,
  "& .MuiTabs-indicator": {
    backgroundColor: palette.navy,
    top: 0,
    height: "4px",
  },
});

const StyledTab = styled(Tab)(({ disabled }) => ({
  margin: "4px 4px 4px 0",
  backgroundColor: disabled ? palette.lightestGray : palette.lightGray,
  color: palette.darkGray,
  flexDirection: "row",

  "&.Mui-selected": {
    backgroundColor: palette.lightestGray,
    color: palette.navy,
    marginBottom: 0,
  },
}));

const CollapseTab = styled(Tab)({
  margin: "4px 0",
  padding: "0 10px",
  backgroundColor: palette.lightestGray,
  minWidth: "12px",
  maxWidth: "12px",
  width: "12px",
  "&:hover": {
    backgroundColor: palette.lightGray,
  },
});

const EMPTY_MESSAGE: Message = { text: "", attachments: [] };

export default function ThreadControls({
  thread,
  refetchThread,
  scrollToCampaign,
  scrollToLatest,
}: {
  thread: Thread;
  refetchThread: () => Promise<any>;
  scrollToCampaign: (campaignId: string) => void;
  scrollToLatest: () => void;
}): ReactElement {
  const snackbar = useSnackbar();
  const intl = useIntl();
  const client = useApolloClient();
  const subaccount = useSubaccountSupervolunteerConfig()!;

  // We need to know what campaign we're using for the thread controls --
  // e.g. which campaign we should show tags/responses from.
  const { contextCampaign, contextCampaignContactId, needsResponseInContext } =
    useContextCampaignAndContact({
      thread,
    });

  const {
    contextCampaignId,
    setContextCampaignId,
    markThreadLoading,
    markThreadLoaded,
    handleNavShortcut,
  } = useThreadsContext()!;

  const { autoadvanceEnabled } = useTexterSettings();

  function maybeAutoadvance() {
    if (autoadvanceEnabled) {
      handleNavShortcut(1);
    }
  }

  const mutations: ThreadMutations = useThreadMutations({
    contextCampaign,
    contextCampaignContactId,
    threadId: thread.id,
    reloadThreadMutations: ["getThread"],
  });

  const [tabsOpen, setTabsOpen] = useState(false);

  // We store the unsaved state at this level of the component tree, rather
  // than in the individual tabs. This lets us centrally managed the "unsaved"
  // warning when you try to navigate away, and also lets us auto-save notes
  // and tags when you send a message and reset your state when you change
  // the context campaign.
  const [unsavedContactNote, setUnsavedContactNote] = useState<string | null>(
    null
  );
  const [mutationPending, setMutationPending] = useState(false);
  const [unsavedTags, setUnsavedTags] = useState<UnsavedTag[]>([]);

  const [removedSavedTagIds, setRemovedSavedTagIds] = useState<string[]>([]);

  const confirmTagRemoval = useConfirm();
  const sendMessageBoxRef = useRef<SendMessageBoxHandle>(null);

  // fetch data: we fetch the contact and the campaign
  // separately (rather than querying for the campaign field
  // on the contact) to take advantage of Apollo caching: we don't
  // want to re-fetch all the script, etc. for a campaign over and
  // over as we navigate between its contacts.
  const contactData = useQuery<
    GetActiveCampaignContactQuery,
    GetActiveCampaignContactQueryVariables
  >(GET_ACTIVE_CAMPAIGN_CONTACT, {
    variables: {
      input: {
        campaignContactId: contextCampaignContactId!,
      },
    },
    skip: !contextCampaignContactId,
  });

  const campaignData = useQuery<
    GetCampaignForTexterQuery,
    GetCampaignForTexterQueryVariables
  >(GET_CAMPAIGN_FOR_TEXTER, {
    variables: {
      input: {
        campaignId: contextCampaign.id!,
      },
    },
    skip: !contextCampaign.id,
  });

  function wrapMutation<A extends any[], R>(
    fn: (...args: A) => Promise<FetchResult<R>>
  ): (...args: A) => Promise<FetchResult<R>> {
    return async (...args) => {
      setMutationPending(true);
      markThreadLoading(thread.id);

      const result = await fn(...args);

      if (!result.errors) {
        // reload "searchThreads" to get updated data for the threads list
        // (threads might move around or disappear from the list after they
        // change state -- e.g. if you're filtered to threads that need a reply,
        // and you send a message, then the thread won't be in the list anymore)
        client
          .refetchQueries({
            include: ["searchThreads"],
          })
          .finally(() => {
            markThreadLoaded(thread.id);
          })
          .catch((e: any) => {
            console.error("Failed to refetch thread search", e);
          });

        // Separately, refetch the count of threads to update the total count
        // (similar to the above, changing a thread might mean there are fewer
        // results for your filters).
        //
        // This is a separate refetch from the above since it might take longer,
        // and we only need to mark the thread as loading while "searchThreads"
        // is refetching
        client
          .refetchQueries({
            include: ["searchThreadsCount"],
          })
          .catch((e: any) => {
            console.error("Failed to refetch thread search count", e);
          });
      } else {
        markThreadLoaded(thread.id);
      }

      setMutationPending(false);
      return result;
    };
  }

  // We wrap all of the mutations: this allows us to alter state when
  // these mutations are called (e.g. auto-saving the note and tags when
  // you send a message; clearing the message when you send it successfully).
  const handleSaveNote = wrapMutation(async () => {
    if (unsavedContactNote == null) {
      throw new Error("Must have an unsaved contact note");
    }

    const result = await mutations.setContactNote(unsavedContactNote);
    if (!result.errors) {
      setUnsavedContactNote(null);
    }

    return result;
  });

  function wrapConfirmTagRemoval<T, U extends unknown[]>(
    f: (...args: U) => Promise<T>
  ): (...args: U) => Promise<T | undefined> {
    return async (...args) => {
      const removedVanTags = contactData.data?.getCampaignContact.tags.filter(
        t =>
          removedSavedTagIds.includes(t.tag.id) &&
          t.tag.integrationActions.length > 0
      );

      if (removedVanTags && removedVanTags.length > 0) {
        const { confirmed } = await confirmTagRemoval({
          title: "",
          description:
            "Note: deleting a tag will only remove it from Scale to Win's data, and will not remove the mapped VAN action from NGP VAN.",
        });
        if (!confirmed) {
          return;
        }
      }

      return f(...args);
    };
  }

  const handleSaveTags = wrapConfirmTagRemoval(
    wrapMutation(async () => {
      const result = await mutations.updateContactTags({
        applyTagIds: unsavedTags.map(t => t.tagId),
        removeTagIds: removedSavedTagIds,
      });
      if (!result.errors) {
        setUnsavedTags([]);
        setRemovedSavedTagIds([]);
      }

      return result!;
    })
  );

  const handleSendMessage = wrapConfirmTagRemoval(
    wrapMutation(async message => {
      const latestMessageId = _.sortBy(thread.messages, [
        m => -m.threadOrderTime,
        m => m.id,
      ])[0].id;

      const sendMessageResult = await mutations.sendMessage({
        latestMessageId,
        message,
        applyTagIds: unsavedTags.map(t => t.tagId),
        removeTagIds: removedSavedTagIds,
        contactNote: unsavedContactNote,
      });

      if (!sendMessageResult.errors) {
        if (
          unsavedTags.length > 0 ||
          unsavedContactNote != null ||
          removedSavedTagIds.length > 0
        ) {
          setUnsavedContactNote(null);
          setUnsavedTags([]);
          setRemovedSavedTagIds([]);

          // If we saved tags or notes, refetch the updated data for the contact
          // (but don't block the rest of the UI -- just the contact details pane
          // will stay loading until this refetch is done).
          contactData
            .refetch()
            .catch(e => console.error("Error refetching contact", e));
        }

        maybeAutoadvance();
        scrollToLatest();

        return sendMessageResult;
      }

      // There were errors: refresh the thread (to get any new message that may
      // have caused a conflict), and the contact (to see if the note/tags were
      // saved even if the message failed).
      //
      // Note that we don't attempt to see whether the message was sent: that's
      // the last thing the mutation does (so it's the least likely to fail), and
      // there's no perfect way to check whether the message was sent (e.g. if
      // the same user sent the same message from another tab, we'd still see
      // that message as sent even if this particular mutation failed). We just
      // scroll to the bottom of the message list and let the user decide what to
      // do next based on the current state.
      await Promise.all([
        refetchThread().catch(e => console.error("Error refetching thread", e)),
        (async () => {
          const newContact = await contactData.refetch();
          if (newContact.data) {
            // check if the note was saved
            if (
              unsavedContactNote != null &&
              newContact.data.getCampaignContact.note?.text ===
                unsavedContactNote
            ) {
              setUnsavedContactNote(null);
            }

            // check if the tags were saved
            const newTagIds = new Set(
              newContact.data.getCampaignContact.tags.map(
                contactTag => contactTag.tag.id
              )
            );
            const stillUnsavedTags = unsavedTags.filter(
              t => !newTagIds.has(t.tagId)
            );
            setUnsavedTags(stillUnsavedTags);

            const notRemovedTags = removedSavedTagIds.filter(tid =>
              newTagIds.has(tid)
            );
            setRemovedSavedTagIds(notRemovedTags);
          }
        })().catch(e => console.error("Error refetching contact", e)),
      ]);

      if (
        graphqlErrorHasCode("WRONG_LATEST_MESSAGE_ID", sendMessageResult.errors)
      ) {
        snackbar.enqueueSnackbar({
          variant: "warning",
          message: intl.formatMessage({
            defaultMessage:
              "Another message was sent or received in this thread. Please review the latest messages and try again.",
            description:
              "Error message when a message fails to send because another message was sent or received in the thread",
          }),
        });
      } else {
        snackbar.enqueueSnackbar({
          variant: "error",
          message: intl.formatMessage({
            defaultMessage: "Failed to send message",
            description: "Error message when we fail to send a message",
          }),
        });
      }

      scrollToLatest();
      return sendMessageResult;
    })
  );

  const handleChangeContextCampaign = (newCampaignId: string) => {
    setUnsavedContactNote(null);
    setUnsavedTags([]);
    setRemovedSavedTagIds([]);
    setContextCampaignId(newCampaignId);
  };

  const handleOptIn = wrapMutation(mutations.markThreadNotOptedOut);

  const handleOptOut = wrapMutation(async () => {
    // clear message
    sendMessageBoxRef.current?.setMessage(EMPTY_MESSAGE);
    handleNavShortcut(1);
    return mutations.markThreadOptedOut();
  });

  const handleMarkNeedsResponse = contextCampaignId
    ? wrapMutation(mutations.markThreadNeedsResponse)
    : null;
  const handleMarkNotNeedsResponse = contextCampaignId
    ? wrapMutation(async () => {
        const result = await mutations.markThreadNotNeedsResponse();

        if (!result.errors) {
          maybeAutoadvance();
        }

        return result;
      })
    : null;

  const handleEscalate = wrapMutation((escalationInboxId: string) =>
    mutations.escalate({ escalationInboxId })
  );

  const handleDeescalate = thread.pinnedToInbox
    ? wrapMutation(mutations.deescalate)
    : null;

  const TABS = [
    "responses",
    "tags",
    "contactDetails",
    "contactHistory",
    "expand",
  ];
  type Tab = (typeof TABS)[number];

  const [activeTab, setActiveTab] = useState<Tab>("responses");

  function handleSelectTab(tab: Tab) {
    if (tab === "expand") {
      setTabsOpen(!tabsOpen);
    } else {
      setTabsOpen(true);
      setActiveTab(tab);
    }
  }

  useMousetrap("shift+space", evt => {
    // prevent the current element from handling the spacebar
    evt.preventDefault();
    document.body.blur();

    if (!tabsOpen) {
      // open first tab
      handleSelectTab(TABS[0]);
      return;
    }

    const newIdx = TABS.indexOf(activeTab) + 1;
    if (newIdx >= TABS.length || TABS[newIdx] === "expand") {
      // close tabs
      setTabsOpen(false);
      sendMessageBoxRef.current?.focusMessageInput();
    } else {
      handleSelectTab(TABS[newIdx]);
    }
  });

  if (contactData.error || campaignData.error) {
    return (
      <ErrorCard
        errors={[contactData.error, campaignData.error].filter(e => !!e)}
      />
    );
  }

  const campaign = campaignData.data?.getCampaign;
  const contact = contactData.data?.getCampaignContact;

  const parsedContactData = (campaign &&
    contact &&
    formatContactData({
      contactData: JSON.parse(contact.data! ?? "{}"),
      campaignContactFields: campaign.contactFields,
    })) || {
    formattedData: {},
    firstName: thread.contactFirstName,
    lastName: thread.contactLastName,
  };

  function renderActiveTab() {
    switch (activeTab) {
      case "responses":
        return (
          <ResponseScripts
            contactData={parsedContactData}
            campaign={campaign}
            isOptedOut={thread.isOptedOut}
            setMessage={(msg, tagAndGroupIds) => {
              if (!mutationPending) {
                sendMessageBoxRef.current?.setMessage(msg);

                setUnsavedTags(prevUnsavedTags =>
                  updateUnsavedTagsForNewResponseScriptTags(
                    tagAndGroupIds,
                    prevUnsavedTags,
                    contactData.data!.getCampaignContact
                  )
                );
              }
            }}
          />
        );
      case "tags":
        return (
          <Tags
            contactTags={contact?.tags}
            campaign={campaign}
            unsavedTags={unsavedTags}
            removedSavedTagIds={removedSavedTagIds}
            addUnsavedTag={(tagId, tagGroupId) => {
              setUnsavedTags([
                ...unsavedTags.filter(t => t.tagId !== tagId),
                { tagId, tagGroupId, appliedBy: TagAppliedBy.Manual },
              ]);
            }}
            removeUnsavedTag={tagId =>
              setUnsavedTags(unsavedTags.filter(t => t.tagId !== tagId))
            }
            removeSavedTag={tagId =>
              setRemovedSavedTagIds([...removedSavedTagIds, tagId])
            }
            cancelRemoveSavedTag={tagId => {
              setRemovedSavedTagIds(
                removedSavedTagIds.filter(id => id !== tagId)
              );
            }}
            mutationPending={mutationPending}
            onSaveTags={handleSaveTags}
          />
        );
      case "contactDetails":
        return (
          <ContactDetails
            campaign={campaign}
            contactData={{ ...parsedContactData, note: contact?.note }}
            handleSaveNote={handleSaveNote}
            mutationPending={mutationPending}
            onChangeUnsavedContactNote={setUnsavedContactNote}
            threadId={thread.id}
            threadToPhoneNumber={thread.toPhoneNumber}
            unsavedContactNote={unsavedContactNote}
          />
        );
      case "contactHistory":
        return (
          <ContactHistory thread={thread} scrollToCampaign={scrollToCampaign} />
        );
    }
  }

  return (
    <>
      <UnsavedChanges
        unsavedMessage={() =>
          sendMessageBoxRef.current?.hasUnsavedChanges() ?? false
        }
        unsavedNote={unsavedContactNote != null}
        unsavedTags={unsavedTags.length > 0 || removedSavedTagIds.length > 0}
        setActiveTab={handleSelectTab}
      />
      <Box sx={{ backgroundColor: palette.lightGray, padding: "24px 40px" }}>
        <SendMessageBox
          ref={sendMessageBoxRef}
          onSubmit={handleSendMessage}
          isOptedOut={thread.isOptedOut}
          mutationPending={mutationPending}
          textingHoursStart={
            campaign?.textingHoursStart ?? subaccount.textingHoursStart
          }
          textingHoursEnd={
            campaign?.textingHoursEnd ?? subaccount.textingHoursEnd
          }
          textingHoursTimezone={
            campaign?.textingHoursTimezone ?? subaccount.textingHoursTimezone
          }
        />
        <Buttons
          isOptedOut={thread.isOptedOut}
          handleOptOut={handleOptOut}
          optInThread={thread}
          handleOptIn={handleOptIn}
          needsResponse={needsResponseInContext}
          handleMarkNeedsResponse={handleMarkNeedsResponse}
          handleMarkNotNeedsResponse={handleMarkNotNeedsResponse}
          handleEscalate={handleEscalate}
          handleDeescalate={handleDeescalate}
          inboxName={thread.pinnedToInbox?.name || null}
          mutationPending={mutationPending}
          ownedPhoneNumber={thread.fromOwnedPhoneNumber}
        />
      </Box>

      <StyledTabs
        value={
          tabsOpen && campaign && contact ? TABS.indexOf(activeTab) : false
        }
        onChange={(evt, val) => handleSelectTab(TABS[val])}
        variant="fullWidth"
      >
        <StyledTab
          label={
            <FormattedMessage
              defaultMessage="Responses"
              description="Tab header for canned responses in the texter view"
            />
          }
          disabled={!campaign}
        />
        <StyledTab
          label={
            <>
              {unsavedTags.length > 0 && (
                <NeedsResponseBullet
                  sx={{ width: "8px", marginRight: "4px" }}
                />
              )}
              <FormattedMessage
                defaultMessage="Tags"
                description="Tab header for tags in the texter view"
              />
            </>
          }
          disabled={!campaign}
        />
        <StyledTab
          label={
            <>
              {unsavedContactNote != null && (
                <NeedsResponseBullet
                  sx={{ width: "8px", marginRight: "4px" }}
                />
              )}
              <FormattedMessage
                defaultMessage="Contact Details"
                description="Tab header for contact details in the texter view"
              />
            </>
          }
          disabled={!campaign}
        />
        <StyledTab
          label={
            <FormattedMessage
              defaultMessage="Contact History"
              description="Tab header for contact history in the texter view"
            />
          }
          disabled={!campaign}
        />
        <CollapseTab label={tabsOpen ? <ExpandMore /> : <ExpandLess />} />
      </StyledTabs>
      <Collapse in={tabsOpen}>
        {tabsOpen &&
          (campaign ? (
            <Box
              sx={{
                height: "280px",
                overflowY: "auto",
                backgroundColor: palette.lightestGray,
                padding: "24px 40px",
              }}
            >
              <ActiveCampaignSwitcher
                contextCampaign={contextCampaign}
                onChange={handleChangeContextCampaign}
                hasUnsavedChanges={() =>
                  unsavedContactNote != null ||
                  unsavedTags.length > 0 ||
                  (sendMessageBoxRef.current?.hasUnsavedChanges() ?? false)
                }
                onDiscardChanges={() => {
                  setUnsavedContactNote(null);
                  setUnsavedTags([]);
                  sendMessageBoxRef.current?.setMessage(EMPTY_MESSAGE);
                }}
                thread={thread}
              />
              {renderActiveTab()}
            </Box>
          ) : (
            <FlexBox
              sx={{ px: 5, py: 3, fontSize: "12px", color: palette.darkGray }}
              justifyContent="center"
            >
              This contact does not currently belong to a campaign.
            </FlexBox>
          ))}
      </Collapse>
    </>
  );
}
